import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useParams, Outlet, Navigate } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { type Locale, URL_PREFIX_TO_LOCALE } from "@/i18n/translations";
import Index from "./pages/Index.tsx";
import NotFound from "./pages/NotFound.tsx";
import ScrollToTop from "./components/ScrollToTop";

const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));

const Accessories = lazy(() => import("./pages/Accessories.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const Blog = lazy(() => import("./pages/Blog.tsx"));
const BlogPost = lazy(() => import("./pages/BlogPost.tsx"));
const ProductDetail = lazy(() => import("./pages/ProductDetail.tsx"));
const ThankYou = lazy(() => import("./pages/ThankYou.tsx"));

const queryClient = new QueryClient();

const LanguageLayout = ({ defaultLocale }: { defaultLocale?: Locale }) => {
  const { lang } = useParams<{ lang: string }>();
  const locale: Locale = defaultLocale ?? (lang && URL_PREFIX_TO_LOCALE[lang] ? URL_PREFIX_TO_LOCALE[lang] : "no");

  if (lang && !URL_PREFIX_TO_LOCALE[lang] && !defaultLocale) {
    return <NotFound />;
  }

  return (
    <LanguageProvider locale={locale}>
      <Outlet />
    </LanguageProvider>
  );
};

const LazyPage = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<div className="min-h-screen bg-background" />}>
    {children}
  </Suspense>
);

const AppRoutes = () => (
  <Routes>
    <Route element={<LanguageLayout defaultLocale="no" />}>
      <Route path="/" element={<Index />} />
      <Route path="/tilbehor" element={<LazyPage><Accessories /></LazyPage>} />
      <Route path="/personvern" element={<LazyPage><PrivacyPolicy /></LazyPage>} />
      <Route path="/vilkar" element={<LazyPage><Terms /></LazyPage>} />
      <Route path="/unsubscribe" element={<LazyPage><Unsubscribe /></LazyPage>} />
      <Route path="/kontakt" element={<LazyPage><Contact /></LazyPage>} />
      <Route path="/blogg" element={<LazyPage><Blog /></LazyPage>} />
      <Route path="/blogg/:slug" element={<LazyPage><BlogPost /></LazyPage>} />
      <Route path="/produkter/:slug" element={<LazyPage><ProductDetail /></LazyPage>} />
      <Route path="/takk" element={<LazyPage><ThankYou /></LazyPage>} />
    </Route>

    <Route path="/:lang" element={<LanguageLayout />}>
      <Route index element={<Index />} />
      <Route path="tilbehor" element={<LazyPage><Accessories /></LazyPage>} />
      <Route path="personvern" element={<LazyPage><PrivacyPolicy /></LazyPage>} />
      <Route path="vilkar" element={<LazyPage><Terms /></LazyPage>} />
      <Route path="unsubscribe" element={<LazyPage><Unsubscribe /></LazyPage>} />
      <Route path="kontakt" element={<LazyPage><Contact /></LazyPage>} />
      <Route path="blogg" element={<LazyPage><Blog /></LazyPage>} />
      <Route path="blogg/:slug" element={<LazyPage><BlogPost /></LazyPage>} />
      <Route path="produkter/:slug" element={<LazyPage><ProductDetail /></LazyPage>} />
      <Route path="takk" element={<LazyPage><ThankYou /></LazyPage>} />
      <Route path="*" element={<NotFound />} />
    </Route>

    <Route path="*" element={<NotFound />} />
  </Routes>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Suspense fallback={null}>
        <Toaster />
        <Sonner />
      </Suspense>
      <BrowserRouter>
        <ScrollToTop />
        <AppRoutes />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
